import { Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";

export default function NutritionTherapy() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const consultations = [
    {
      id: 1,
      title: "Discovery call",
      tag: "Step 1",
      format: "Video or phone",
      idealFor: "New clients and curious first-time conversations",
      includes: [
        "Clarify your goals and expectations",
        "Check if we're the right fit",
        "Outline next steps if we continue",
      ],
      description:
        "This is an opportunity for me to find out if I am able to help you, as there are certain circumstances that are out of my current scope of practice",
      time: "20 minutes",
    },
    {
      id: 2,
      title: "Initial consultation",
      tag: "Step 2",
      format: "Video consultation",
      idealFor: "Clients ready to start a structured plan",
      includes: [
        "Deep dive into your health history",
        "Review your questionnaire and goals",
        "Personalised dietary and lifestyle recommendations",
      ],
      description:
        "In this consultation we go through a questionnaire that you would have filled in prior to our session, and we will explore your goals and circumstances and will give me an opportunity to gather all the necessary information. I will also establish initial dietary and lifestyle recommendations that would start your journey towards your goals",
      time: "90 minutes",
    },
    {
      id: 3,
      title: "Follow-up consultation",
      tag: "Step 3",
      format: "Video consultation",
      idealFor: "Refining your plan and building momentum",
      includes: [
        "Review progress and adjust your plan",
        "Troubleshoot barriers and challenges",
        "Align changes with updated goals or results",
      ],
      description:
        "Here we explore how you found the recommended programme. Depending on it, I would suggest staying on it or modifying it to better suit your needs. You might need more time to adjust to the changes, or you might be ready to add a second layer to the programme. Everyone is different in the way they implement changes, and I do not set timeframes. We will also discuss if your goals and priorities have changed, or if there are any new test results to consider in order to inform further strategies",
      time: "45 minutes",
    },
    {
      id: 4,
      title: "Maintenance consultation",
      tag: "Step 4",
      format: "Video consultation",
      idealFor: "Staying on track during life changes",
      includes: [
        "Check-in on routines and confidence",
        "Support through transitions or setbacks",
        "Fine-tune sustainable habits",
      ],
      description:
        "I offer this consultation as I understand that change is an ongoing process, and clients might need reassurance in knowing they are on the right path, or might need a quick catch up when circumstances change",
      time: "30 minutes",
    },
    {
      id: 5,
      title: "Email support",
      tag: "Between sessions",
      format: "Email",
      idealFor: "Quick questions between appointments",
      includes: [
        "Clarify recommendations",
        "Support with small adjustments",
        "Response within 24 hours",
      ],
      description:
        "I am available for a quick email inquiry should there be a question or concern between sessions. I will aim to respond within 24 hours of receiving the email.",
    },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-b bg-primary to-green-400/10">
      <Helmet>
        <title>
          Nutritional Therapy Process | Consultation Types & Services
        </title>
        <meta
          name="description"
          content="Explore the nutritional therapy process with Aleks. From discovery calls to ongoing maintenance support, discover each consultation type and how they support your health goals."
        />
        <meta
          name="keywords"
          content="nutritional therapy process, consultation types, discovery call, initial consultation, follow-up consultation, email support"
        />
        <meta
          property="og:title"
          content="Nutritional Therapy Process & Consultation Services"
        />
        <meta
          property="og:description"
          content="Learn about the four-step nutritional therapy process including discovery calls, consultations, and ongoing support to transform your health."
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
        {/* Page Header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            Nutritional Therapy
          </h1>
          <div className="w-24 h-1 bg-gray-300 mx-auto rounded-full"></div>
        </div>

        <div className="lg:flex justify-between pb-2">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-left text-white leading-tight mb-4">
            The process
          </h2>
          <div className="space-x-2">
            <button
              onClick={() =>
                window.open(
                  "https://www.bookable.page/aleksandra",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="bg-[#F8F9FA] text-primary font-semibold px-8 py-2 rounded-xl shadow-lg transition-all duration-300 text-base sm:text-lg hover:bg-[#EEF1F3] hover:shadow-2xl hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            >
              Book Discovery Call
            </button>
          </div>
        </div>

        {/* Consultation Cards Grid */}
        {/* Mobile Carousel */}
        <div className="md:hidden">
          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-300"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {consultations.map((consultation) => (
                <article
                  key={consultation.id}
                  className="relative w-full flex-shrink-0"
                >
                  <div className="relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 sm:p-8 hover:shadow-xl transition-all duration-300 flex flex-col h-full">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <p className="text-xs text-left uppercase tracking-wider text-primary/70 font-semibold mb-2">
                          {consultation.tag}
                        </p>
                        <h2 className="text-lg text-left sm:text-xl font-bold text-gray-900">
                          {consultation.title}
                        </h2>
                      </div>
                      {consultation.time && (
                        <div className="bg-gradient-to-r from-primary/10 to-green-400/10 rounded-full px-3 py-1.5 border border-primary/10 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-primary/70 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-700">
                            {consultation.time}
                          </span>
                        </div>
                      )}
                    </div>

                    <p className="text-sm text-gray-700 text-left leading-relaxed mb-4">
                      {consultation.description}
                    </p>

                    <div className="mt-auto text-left">
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="text-xs sm:text-sm bg-green-400/10 text-gray-700 px-3 py-1 rounded-full">
                          {consultation.idealFor}
                        </span>
                      </div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                        What's included
                      </p>
                      <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                        {consultation.includes.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Carousel Controls */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={() =>
                setCurrentSlide((prev) =>
                  prev === 0 ? consultations.length - 1 : prev - 1,
                )
              }
              className="bg-white/95 text-primary p-2 rounded-full shadow-lg hover:shadow-xl hover:bg-white transition-all duration-300"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex gap-2">
              {consultations.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide ? "bg-white w-6" : "bg-white/50 w-2"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={() =>
                setCurrentSlide((prev) =>
                  prev === consultations.length - 1 ? 0 : prev + 1,
                )
              }
              className="bg-white/95 text-primary p-2 rounded-full shadow-lg hover:shadow-xl hover:bg-white transition-all duration-300"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid gap-6 md:grid-cols-2">
          {consultations.map((consultation) => (
            <article key={consultation.id} className="relative">
              <div className="relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 sm:p-8 hover:shadow-xl transition-all duration-300 flex flex-col h-full">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="text-xs text-left uppercase tracking-wider text-primary/70 font-semibold mb-2">
                      {consultation.tag}
                    </p>
                    <h2 className="text-lg text-left sm:text-xl font-bold text-gray-900">
                      {consultation.title}
                    </h2>
                  </div>
                  {consultation.time && (
                    <div className="bg-gradient-to-r from-primary/10 to-green-400/10 rounded-full px-3 py-1.5 border border-primary/10 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary/70 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700">
                        {consultation.time}
                      </span>
                    </div>
                  )}
                </div>

                <p className="text-sm text-gray-700 text-left leading-relaxed mb-4">
                  {consultation.description}
                </p>

                <div className="mt-auto text-left">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-xs sm:text-sm bg-green-400/10 text-gray-700 px-3 py-1 rounded-full">
                      {consultation.idealFor}
                    </span>
                  </div>
                  <p className="text-xs uppercase  tracking-wider text-gray-500 font-semibold mb-2">
                    What’s included
                  </p>
                  <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                    {consultation.includes.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
